import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: any) => ({
  accordion: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: 0,
      marginBottom: theme.spacing(2),
    },
  },
  summary: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    '&.Mui-expanded': {
      backgroundColor: theme.palette.background.default,
      minHeight: 'auto',
    },
  },
  summaryContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    '&.Mui-expanded': {
      margin: 0,
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  summaryTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    wordBreak: 'break-word',
  },
  summaryIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.primary,
    flexShrink: 0,
  },
  summaryAmount: {
    fontSize: '18px',
    fontWeight: 700,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
  },
  expandIcon: {
    color: theme.palette.text.secondary,
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  section: {
    marginBottom: theme.spacing(3),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1.5),
    letterSpacing: '0.5px',
    wordBreak: 'break-word',
  },
  explanation: {
    fontSize: '13px',
    color: theme.palette.text.primary,
    lineHeight: 1.6,
    marginBottom: theme.spacing(1.5),
    wordBreak: 'break-word',
  },
  breakdownRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    '&:last-child': {
      borderBottom: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  breakdownLabel: {
    fontSize: '13px',
    color: theme.palette.text.primary,
    wordBreak: 'break-word',
  },
  breakdownAmount: {
    fontSize: '14px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5, 0),
    marginTop: theme.spacing(1),
    borderTop: `2px solid ${theme.palette.divider}`,
    fontWeight: 700,
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  totalLabel: {
    fontSize: '14px',
    color: theme.palette.text.primary,
    wordBreak: 'break-word',
  },
  totalAmount: {
    fontSize: '16px',
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
  },
  mainExplanation: {
    fontSize: '13px',
    color: theme.palette.text.primary,
    lineHeight: 1.6,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
}));

interface CommissionsSectionProps {
  commissionsTotal?: number; // in cents
}

/**
 * CommissionsSection - Expandable accordion showing real estate commissions breakdown
 *
 * Displays:
 * - Total commissions ($17,500)
 * - Selling Agent commission ($14,000)
 * - Listing Agent commission ($3,500)
 * - Plain English explanation of what commissions are and who pays
 *
 * All text is internationalized via i18n keys from 'closingDisclosure' namespace.
 * Amounts are displayed in cents and converted to USD currency format.
 */
const CommissionsSection: React.FC<CommissionsSectionProps> = ({
  commissionsTotal = 1750000, // Default: $17,500 in cents
}) => {
  const { t } = useTranslation('closingDisclosure');
  const classes = useStyles();

  /**
   * Format amount in cents to currency string with commas and 2 decimals
   * Example: 1750000 cents → "$17,500.00"
   */
  const formatCurrency = (amountCents: number | undefined | null): string => {
    if (amountCents === undefined || amountCents === null) {
      return '$0.00';
    }
    const amountDollars = amountCents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amountDollars);
  };

  // Calculate individual commissions as percentages of total
  // Typical split: Selling Agent 2%, Listing Agent 0.5% (can be customized)
  // For order 00001: Selling Agent $14,000, Listing Agent $3,500
  const sellingAgentCommission = Math.round(commissionsTotal * (14000 / 17500));
  const listingAgentCommission = commissionsTotal - sellingAgentCommission;

  return (
    <Accordion className={classes.accordion}>
      <AccordionSummary
        expandIcon={
          <FontAwesomeIcon
            icon="chevron-down"
            className={classes.expandIcon}
          />
        }
        className={classes.summary}
      >
        <Box className={classes.summaryContent}>
          <Typography className={classes.summaryTitle}>
            <FontAwesomeIcon
              icon="handshake"
              className={classes.summaryIcon}
            />
            {t('commissions-section')}
          </Typography>
          <Typography className={classes.summaryAmount}>
            {formatCurrency(commissionsTotal)}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails className={classes.details}>
        <Box>
          {/* Main Explanation */}
          <Typography className={classes.mainExplanation}>
            {t('commissions-what-is')}
          </Typography>

          {/* Selling Agent Commission */}
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              {t('commissions-selling')}
            </Typography>
            <Typography className={classes.explanation}>
              {t('commissions-selling-detail')}
            </Typography>
            <Box className={classes.breakdownRow}>
              <Typography className={classes.breakdownLabel}>
                {t('commissions-selling')}
              </Typography>
              <Typography className={classes.breakdownAmount}>
                {formatCurrency(sellingAgentCommission)}
              </Typography>
            </Box>
          </Box>

          {/* Listing Agent Commission */}
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              {t('commissions-listing')}
            </Typography>
            <Typography className={classes.explanation}>
              {t('commissions-listing-detail')}
            </Typography>
            <Box className={classes.breakdownRow}>
              <Typography className={classes.breakdownLabel}>
                {t('commissions-listing')}
              </Typography>
              <Typography className={classes.breakdownAmount}>
                {formatCurrency(listingAgentCommission)}
              </Typography>
            </Box>
          </Box>

          {/* Total */}
          <Box className={classes.totalRow}>
            <Typography className={classes.totalLabel}>
              {t('commissions-title')}
            </Typography>
            <Typography className={classes.totalAmount}>
              {formatCurrency(commissionsTotal)}
            </Typography>
          </Box>

          {/* Who Pays Section */}
          <Box className={classes.section} sx={{ marginTop: 2 }}>
            <Typography className={classes.sectionTitle}>
              {t('commissions-who-pays')}
            </Typography>
            <Typography className={classes.explanation}>
              {t('commissions-why')}
            </Typography>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default CommissionsSection;
